import { arrayMove } from '@dnd-kit/sortable';

import localStorageService from '@/services/local-storage/local-storage';

export type ManualOrderMoveAction = 'top' | 'bottom' | 'up' | 'down';

export type ManualOrderConfig<T = unknown> = {
  storageKey: string;
  scopeKey: string;
  getRowId?: (row: T) => string;
  mapOrderedItem?: (item: T, index: number) => T;
};

export type ManualOrderMenuState = {
  canMoveTop: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canMoveBottom: boolean;
};

type ManualOrderStore = Record<string, string[]>;

const defaultGetRowId = <T>(row: T): string => {
  const candidate = row as { id?: string };
  return candidate.id ?? '';
};

export const getManualOrderRowId = <T>(row: T, getRowId?: (row: T) => string): string => (getRowId ?? defaultGetRowId)(row);

export const getManualOrderSignature = <T>(items: T[], getRowId?: (row: T) => string): string =>
  items.map((item) => getManualOrderRowId(item, getRowId)).join('|');

const readManualOrderStore = (storageKey: string): ManualOrderStore => {
  const stored = localStorageService.getItem(storageKey);
  return stored && typeof stored === 'object' ? (stored as ManualOrderStore) : {};
};

export const loadManualOrderIds = (storageKey: string, scopeKey: string): string[] | null => {
  if (!scopeKey) {
    return null;
  }

  const store = readManualOrderStore(storageKey);
  const ids = store[scopeKey];
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string' && Boolean(id)) : null;
};

export const saveManualOrderIds = (storageKey: string, scopeKey: string, orderedIds: string[]): void => {
  if (!scopeKey) {
    return;
  }

  const store = readManualOrderStore(storageKey);
  store[scopeKey] = orderedIds;
  localStorageService.setItem(storageKey, store);
};

export const applyManualOrder = <T>(items: T[], storedIds: string[] | null | undefined, getRowId?: (row: T) => string): T[] => {
  if (!storedIds?.length) {
    return items;
  }

  const resolveRowId = getRowId ?? defaultGetRowId;
  const byId = new Map(items.map((item) => [resolveRowId(item), item]));
  const ordered: T[] = [];
  const seen = new Set<string>();

  storedIds.forEach((id) => {
    const item = byId.get(id);
    if (item) {
      ordered.push(item);
      seen.add(id);
    }
  });

  items.forEach((item) => {
    const id = resolveRowId(item);
    if (!seen.has(id)) {
      ordered.push(item);
    }
  });

  return ordered;
};

export const mapManualOrderItems = <T>(items: T[], mapOrderedItem?: (item: T, index: number) => T): T[] =>
  mapOrderedItem ? items.map((item, index) => mapOrderedItem(item, index)) : items;

export const reorderByDrag = <T>(items: T[], prevId: string, newId: string, getRowId?: (row: T) => string): T[] => {
  const resolveRowId = getRowId ?? defaultGetRowId;
  const oldIndex = items.findIndex((item) => resolveRowId(item) === prevId);
  const newIndex = items.findIndex((item) => resolveRowId(item) === newId);

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return items;
  }

  return arrayMove(items, oldIndex, newIndex);
};

export const reorderByAction = <T>(
  items: T[],
  rowId: string,
  action: ManualOrderMoveAction,
  getRowId?: (row: T) => string
): T[] => {
  const resolveRowId = getRowId ?? defaultGetRowId;
  const currentIndex = items.findIndex((item) => resolveRowId(item) === rowId);

  if (currentIndex === -1) {
    return items;
  }

  let targetIndex = currentIndex;

  switch (action) {
    case 'top':
      targetIndex = 0;
      break;
    case 'bottom':
      targetIndex = items.length - 1;
      break;
    case 'up':
      targetIndex = currentIndex - 1;
      break;
    case 'down':
      targetIndex = currentIndex + 1;
      break;
    default:
      return items;
  }

  if (targetIndex < 0 || targetIndex >= items.length || targetIndex === currentIndex) {
    return items;
  }

  return arrayMove(items, currentIndex, targetIndex);
};

export const getManualOrderMenuState = <T>(items: T[], rowId: string, getRowId?: (row: T) => string): ManualOrderMenuState => {
  const resolveRowId = getRowId ?? defaultGetRowId;
  const index = items.findIndex((item) => resolveRowId(item) === rowId);
  const lastIndex = items.length - 1;

  if (index === -1 || items.length <= 1) {
    return {
      canMoveTop: false,
      canMoveUp: false,
      canMoveDown: false,
      canMoveBottom: false,
    };
  }

  return {
    canMoveTop: index > 0,
    canMoveUp: index > 0,
    canMoveDown: index < lastIndex,
    canMoveBottom: index < lastIndex,
  };
};
